import * as React from "react";
import { Slot } from "@radix-ui/react-slot";

import { cn } from "../../lib/utils";

/** The core unit of the redesign: a fixed-height, keyboard-addressable dense list row. */

export interface ListRowProps extends React.HTMLAttributes<HTMLElement> {
  /** Row is part of the current selection. Adds the 2px accent marker. */
  selected?: boolean;
  /** The j/k keyboard cursor is on this row. */
  focused?: boolean;
  /** 36px (`dense`) or 40px (`comfortable`). Default `comfortable`. */
  density?: "dense" | "comfortable";
  /** Renders the row as an `<a>` — use for rows that are genuine navigation. */
  href?: string;
  /** Render onto the child element (e.g. a router `<Link>`) instead. */
  asChild?: boolean;
  /** Left slot: StatusDot / CircularProgress / checkbox. */
  leading?: React.ReactNode;
  /** Right slot: avatars, dates, counts. Right-aligned tertiary meta. */
  trailing?: React.ReactNode;
  /** The row title (plus any inline meta). */
  children: React.ReactNode;
  /**
   * Optional sub-line under the title. Its presence turns the row into the
   * fixed two-line variant (`h-row-xl`, 64px) — both lines still truncate, so
   * every row in the list stays exactly one height (§4 fixed-height rows).
   */
  secondary?: React.ReactNode;
  /** Mirrors `focused` for parents that spread `getRowProps()` directly. */
  "data-focused"?: boolean | "true" | "false";
}

const densityClass = {
  dense: "h-row",
  comfortable: "h-row-lg",
} as const;

const ListRow = React.forwardRef<HTMLElement, ListRowProps>(
  (
    {
      selected = false,
      focused,
      density = "comfortable",
      href,
      asChild = false,
      leading,
      trailing,
      children,
      secondary,
      className,
      role,
      tabIndex,
      "data-focused": dataFocused,
      ...props
    },
    ref,
  ) => {
    const isFocused =
      focused ?? (dataFocused === true || dataFocused === "true");
    const resolvedRole = role ?? "row";

    const classes = cn(
      "group relative flex w-full select-none items-center gap-2 px-4",
      // No row rule: rows are separated by `hover:bg-hover` and the title/meta
      // type hierarchy (`docs/borderless-contract.md` §1).
      "cursor-default text-sm text-text",
      "transition-[background-color] duration-100 ease-out-fast",
      "hover:bg-hover",
      "focus-visible:bg-hover focus-visible:outline-none",
      secondary ? "h-row-xl" : densityClass[density],
      selected &&
        cn(
          "bg-selected",
          "before:absolute before:left-0 before:top-0 before:h-full before:w-0.5",
          "before:bg-accent",
        ),
      isFocused && "bg-hover",
      href && "no-underline",
      className,
    );

    const renderContent = (title: React.ReactNode) => (
      <>
        {leading ? (
          <span className="flex shrink-0 items-center">{leading}</span>
        ) : null}
        {secondary ? (
          <span className="flex min-w-0 flex-1 flex-col justify-center gap-0.5">
            <span className="truncate font-medium text-text">{title}</span>
            <span className="truncate text-xs font-normal text-text-tertiary">
              {secondary}
            </span>
          </span>
        ) : (
          <span className="min-w-0 flex-1 truncate font-medium text-text">
            {title}
          </span>
        )}
        {trailing ? (
          <span className="ml-auto flex shrink-0 items-center gap-2 text-xs tabular-nums text-text-tertiary">
            {trailing}
          </span>
        ) : null}
      </>
    );

    const content = renderContent(children);

    const shared = {
      "data-slot": "list-row",
      "data-focused": isFocused ? "true" : "false",
      "data-state": selected ? "selected" : undefined,
      role: resolvedRole,
      tabIndex: tabIndex ?? -1,
      "aria-selected": resolvedRole === "option" ? selected : undefined,
      className: classes,
      ...props,
    };

    // `asChild` renders onto a consumer element (e.g. a router `<Link>`); that
    // element's own children become the row title.
    if (asChild && React.isValidElement(children)) {
      const child = children as React.ReactElement<{
        children?: React.ReactNode;
      }>;
      return (
        <Slot ref={ref as React.Ref<HTMLElement>} {...shared}>
          {React.cloneElement(
            child,
            undefined,
            renderContent(child.props.children),
          )}
        </Slot>
      );
    }

    if (href) {
      return (
        <a ref={ref as React.Ref<HTMLAnchorElement>} href={href} {...shared}>
          {content}
        </a>
      );
    }

    return (
      <div ref={ref as React.Ref<HTMLDivElement>} {...shared}>
        {content}
      </div>
    );
  },
);
ListRow.displayName = "ListRow";

export { ListRow };
